/**
 * statusFill — central palette + half-fill progression math for the
 * tooth chart. One module so every renderer (ArchOdontogram,
 * AnatomicalTooth, Odontogram, ToothSVG, the summary PLAN dot, the
 * legend) speaks the same visual language.
 *
 * The dentist's mental model maps a sitting's lifecycle to a bottom-up
 * fill height — empty when there's nothing, partially blue when planned,
 * teal at the mid-point when work is actively in progress, full green
 * when the procedure is done. Color + fill height together make the
 * state legible at arm's length without reading text.
 *
 * The same teal mid-color is reused for "active focus" UI (quadrant
 * banner + selection ring) so the dentist learns one rule:
 *   teal = "the work is in flight or the focus is here".
 */

// ── Canonical palette ─────────────────────────────────────────────
//
// `PALETTE.in_progress` is the active-work teal. It deliberately
// matches the quadrant-focus banner background tint and the whole-tooth
// selection ring color (rgba forms below) so all "in flight" UI shares
// one perceptual key.

export const PALETTE = {
  // Procedure-lifecycle progression
  planned:     '#2563eb', // blue-600  — START
  in_progress: '#0d9488', // teal-600  — MIDDLE (active work)
  completed:   '#16a34a', // green-600 — END

  // Diagnosis severity (separate axis — drawn over the progression dot)
  diagnosis_severe:   '#dc2626', // red-600
  diagnosis_moderate: '#ea580c', // orange-600
  diagnosis_mild:     '#ca8a04', // yellow-600
};

// rgba forms for translucent fills (selection backdrop, quadrant zone).
export const PALETTE_RGBA = {
  active_focus_strong: 'rgba(13, 148, 136, 0.18)', // selection backdrop
  active_focus_soft:   'rgba(13, 148, 136, 0.10)', // quadrant zone tint
  active_focus_outline:'rgba(13, 148, 136, 0.85)', // hover glow
};


// ── Status → fill mapping ─────────────────────────────────────────
//
// Maps a SittingStatus enum string to:
//   - level: 0..1 — bottom-up fill height
//   - color: hex along the blue → teal → green ramp
//
// The level steps are intentionally non-uniform: the dentist needs to
// distinguish "just queued" (level ≈ 0.25, blue) from "actively working
// on it" (level ≈ 0.7, teal) at a glance. Anything between is
// administrative (payment, hold) and lives near the start.

export const STATUS_FILL = {
  // Start (queued / waiting)
  planned:           { level: 0.25, color: PALETTE.planned },
  scheduled:         { level: 0.30, color: PALETTE.planned },
  awaiting_payment:  { level: 0.35, color: '#3b82f6' },   // blue-500
  payment_cleared:   { level: 0.45, color: '#0ea5e9' },   // sky-500
  held:              { level: 0.50, color: '#06b6d4' },   // cyan-500
  ready:             { level: 0.55, color: PALETTE.in_progress },

  // Middle (active work)
  in_progress:       { level: 0.70, color: PALETTE.in_progress },

  // Late / approval / end
  pending_approval:  { level: 0.85, color: '#10b981' },   // emerald-500
  approved:          { level: 0.90, color: '#10b981' },
  completed:         { level: 1.00, color: PALETTE.completed },
  paid:              { level: 1.00, color: PALETTE.completed },

  // Terminal / inactive
  cancelled:         { level: 0.00, color: '#94a3b8' },   // slate-400
  voided:            { level: 0.00, color: '#94a3b8' },
  rejected:          { level: 0.00, color: '#94a3b8' },
  skipped:           { level: 0.00, color: '#94a3b8' },
};

// Default for unknown or missing status — show nothing.
const FALLBACK = { level: 0, color: '#94a3b8' };


function lookup(status) {
  if (!status) return FALLBACK;
  const key = String(status).toLowerCase();
  return STATUS_FILL[key] || FALLBACK;
}

export function statusFillLevel(status) {
  return lookup(status).level;
}

export function statusFillColor(status) {
  return lookup(status).color;
}

/**
 * isActiveFocusStatus — true for any sitting status that should read
 * as "work in flight" (teal palette). Used by the chart for ring
 * coloring + by the summary PLAN dot color selection.
 */
export function isActiveFocusStatus(status) {
  const key = String(status || '').toLowerCase();
  return key === 'in_progress' || key === 'ready' || key === 'pending_approval'
    || key === 'approved';
}


// ── Render helper: build the half-fill SVG <g> ────────────────────
//
// Returns a description object (color, height, y-origin) that callers
// turn into JSX. Kept as a pure function so the same maths drives the
// arch, legacy grid, and legend examples.

export function describeHalfFill({ status, radius = 4 }) {
  const { level, color } = lookup(status);
  const dotHeight = radius * 2;
  const fillHeight = dotHeight * level;
  const fillY = dotHeight - fillHeight; // bottom-up: fill starts from low Y
  return {
    color,
    level,
    radius,
    dotHeight,
    fillHeight,
    fillY,
    // Background ring (the END frame, faint green) — always shown so
    // the dentist sees how far there is to go.
    frameColor: PALETTE.completed,
    frameOpacity: 0.15,
  };
}
